import React, { useEffect } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';
import './Toast.css';

// A transient confirmation, bottom-right.
//
// WHY IT IS SHARED. Three pages had already grown their own — TaskReconciliationPage,
// TaskConfigurationPage and ErrorAnalysisPage each keep a `toast` state, a `showToast` helper and a
// near-identical block of CSS, and they have already drifted (4s in one, 5s in another; different
// class names for the same three kinds). This is the fourth caller, and a fourth copy is where a
// pattern stops being a pattern. The existing three can adopt it whenever they are next touched.
//
// FOR CONFIRMING SOMETHING THAT ALREADY HAPPENED — a send, a save, a delete — where the work is done
// and the reader only needs to know it worked. Anything they must act on belongs on the page, not in a
// message that removes itself after four seconds.

const ICONS = { success: CheckCircle, error: AlertCircle, info: Info };

/**
 * @param message  what happened, in the past tense
 * @param kind     'success' | 'error' | 'info'
 * @param onClose  called when it dismisses itself, or when the reader closes it
 * @param duration ms before it goes; an ERROR stays until dismissed, because an error the reader
 *                 missed is an error they will hit again
 */
export default function Toast({ message, kind = 'success', onClose, duration = 4500 }) {
    useEffect(() => {
        if (!message || kind === 'error') return undefined;
        const t = setTimeout(() => onClose && onClose(), duration);
        return () => clearTimeout(t);
    }, [message, kind, duration, onClose]);

    if (!message) return null;
    const Icon = ICONS[kind] || Info;

    return (
        <div className={`hc-toast hc-toast-${kind}`} role="status" aria-live="polite">
            <Icon size={18} className="hc-toast-icon" />
            <span className="hc-toast-message">{message}</span>
            <button className="hc-toast-close" onClick={onClose} aria-label="Dismiss">
                <X size={15} />
            </button>
        </div>
    );
}
